import { createFeedback } from './core'
import type { FeedbackApi, FeedbackConfig } from './types'

type QueuedCall =
  | ['init', FeedbackConfig]
  | ['identify', NonNullable<FeedbackConfig['user']>]
  | ['setMetadata', Record<string, unknown>]
  | ['open', Parameters<FeedbackApi['open']>[0]]

interface WindowLike {
  Feedback?: {
    q?: QueuedCall[]
    (name: string, arg?: unknown): void
  }
}

const w = window as unknown as WindowLike

function resolveConfigFromScriptTag(): FeedbackConfig | null {
  const script = document.currentScript as HTMLScriptElement | null
  if (!script) return null
  const apiKey = script.dataset.key
  const endpoint = script.dataset.endpoint
  if (!apiKey || !endpoint) return null
  const env = script.dataset.env as FeedbackConfig['env'] | undefined
  return {
    apiKey,
    endpoint,
    ...(env !== undefined && { env }),
  }
}

let api: FeedbackApi | null = null
const pending: QueuedCall[] = w.Feedback?.q ?? []

function handle(call: QueuedCall) {
  const [name, arg] = call
  if (name === 'init') {
    if (api) return
    api = createFeedback(arg as FeedbackConfig)
    return
  }
  if (!api) {
    pending.push(call)
    return
  }
  if (name === 'identify') api.identify(arg as NonNullable<FeedbackConfig['user']>)
  else if (name === 'setMetadata') api.setMetadata(arg as Record<string, unknown>)
  else if (name === 'open') api.open(arg as Parameters<FeedbackApi['open']>[0])
}

const autoConfig = resolveConfigFromScriptTag()
if (autoConfig) api = createFeedback(autoConfig)

while (pending.length > 0) handle(pending.shift()!)

const FeedbackFn = Object.assign(
  function Feedback(name: string, arg?: unknown) {
    handle([name, arg] as QueuedCall)
  },
  { q: pending },
)

w.Feedback = FeedbackFn
